import { Divider, Typography, Row, Col, Card, Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import moment from 'moment';
import './QuotationPreview.css';

const { Title, Text } = Typography;

const formatNumber = (num) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(num || 0);

const QuotationPreview = ({ data, overrideCreatedAt, addressBlock }) => {
  if (!data) return null;

  // Print function
  const handlePrint = () => {
    window.print();
  };

  const {
    quotation_no,
    items = [],
    student_name,
    address,
    phone,
    subtotal,
    discount,
    vat,
    total_amount,
    created_by_firstname,
    created_by_lastname,
    unique_id,
    expiration_days = 3 
  } = data;

  // Calculate expiration date based on expiration_days
    const createdAtToUse = overrideCreatedAt || data.created_at;

  const calculateExpirationDate = () => {
    if (!createdAtToUse) return moment().add(data.expiration_days || 3, 'days').format('DD MMM YY');
    return moment(createdAtToUse).add(data.expiration_days || 3, 'days').format('DD MMM YY');
  };

  // Generate QR code data
  const qrCodeData = unique_id || quotation_no || `QO-${moment().format('YYYYMMDDHHmm')}`;

  // Calculate totals for summary
  const totalAfterDiscount = subtotal - discount;

  // Helper function to get expiration text
  const getExpirationText = () => {
    const days = expiration_days;
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days === 7) return '1 week';
    if (days === 15) return '15 days';
    if (days === 30) return '1 month';
    return `${days} days`;
  };

  return (
    <div id="quotation-preview-content" style={{ width: '100%' }}>
      {/* Header Row */}
      <Row justify="space-between" align="top" style={{ marginBottom: 24 }}>
        <Col span={14}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <img
              src="/images/tfulogo.png"
              alt="TFU Logo"
              style={{ width: 150, height: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <Title level={4} style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '16px' }}>
                TFU Educational Services Co.,Ltd.
              </Title>
              <div style={{ fontSize: 12, lineHeight: '1.6' }}>
                {addressBlock}
                <div>TAX ID 0125554005518</div>
                <div>Tel: 02-574-2671</div>
                <div>Email: info@tfueducation.co.th</div>
              </div>
            </div>
          </div>
        </Col>
        <Col span={10}>
          <Card
            size="small"
            style={{
              backgroundColor: '#f0f5ff',
              border: '2px solid #d6e4ff',
              textAlign: 'center',
              marginLeft: 'auto',
              maxWidth: 280
            }}
          >
            <Title level={3} style={{ color: '#4646ff', margin: '0 0 12px 0', fontSize: '24px' }}>
              QUOTATION
            </Title>
            <div style={{ fontSize: 12 }}>
              <Row justify="space-between" style={{ marginBottom: '4px' }}>
                <Text strong>No:</Text>
                <Text>{quotation_no}</Text>
              </Row>
              <Row justify="space-between" style={{ marginBottom: '4px' }}>
                <Text strong>Date:</Text>
                <Text>{moment(createdAtToUse).format('DD MMM YY')}</Text>
                </Row>
              <Row justify="space-between" style={{ marginBottom: '4px' }}>
                <Text strong>Valid for:</Text>
                <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {getExpirationText()}
                </Text>
              </Row>
              <Row justify="space-between">
                <Text strong>Expires:</Text>
                <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {calculateExpirationDate()}
                </Text>
              </Row>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Customer Info as Card */}
      <Title level={5} style={{ marginBottom: 12 }}>Customer Information</Title>
      <Card 
        size="small" 
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[24, 8]}>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ display: 'inline-block', width: 60 }}>Name:</Text>
              <Text>{student_name || '-'}</Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ display: 'inline-block', width: 60 }}>Phone:</Text>
              <Text>{phone || '-'}</Text>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Address:</Text>
                <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {address || '-'}
                </Text>
            </div>
            </Col>
        </Row>
      </Card>

      {/* Items as Rows */}
      <Title level={5} style={{ marginBottom: 12 }}>Quotation Items</Title>
      <Card 
        size="small" 
        style={{ marginBottom: 16 }}
      >
        {/* Header Row */}
        <div style={{
          backgroundColor: '#f0f0fa',
          padding: '12px 16px',
          borderBottom: '1px solid #d9d9d9',
          fontSize: 13,
          fontWeight: 'bold'
        }}>
          <Row>
            <Col span={2} style={{ textAlign: 'center' }}>#</Col>
            <Col span={10}>Description</Col>
            <Col span={4} style={{ textAlign: 'right' }}>Quantity</Col>
            <Col span={4} style={{ textAlign: 'right' }}>Unit Price</Col>
            <Col span={4} style={{ textAlign: 'right' }}>Total</Col>
            </Row>
        </div>

        {/* Item Rows */}
       {items.map((item, index) => (
            <div 
                key={index}
                style={{
                padding: '5px 0px',
                borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                backgroundColor: index % 2 === 1 ? '#f8f8ff' : 'transparent',
                fontSize: 13
                }}
            >
                <Row align="middle">
                <Col span={2} style={{ textAlign: 'center' }}>
                    <Text>{index + 1}</Text>
                </Col>
                <Col span={10}>
                    <Text>{item.description}</Text>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                    <Text>{item.quantity}</Text>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                    <Text>฿{formatNumber(item.unit_price)}</Text>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                    <Text>฿{formatNumber(item.amount)}</Text>
                </Col>
                </Row>
            </div>
            ))}
      </Card>

      {/* Totals Summary */}
      <Row justify="end" style={{ marginBottom: 10 }}>
        <Col span={8}>
          <div style={{ 
            fontSize: 13,
            lineHeight: 1.6,
            border: '1px solid #d9d9d9',
            padding: 16,
            backgroundColor: '#fafafa',
            borderRadius: 6
          }}>
            <Row justify="space-between" style={{ marginBottom: 4 }}>
              <Text>Sub Total:</Text>
              <Text>{formatNumber(subtotal)}</Text>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 4 }}>
              <Text>Discount:</Text>
              <Text>{formatNumber(discount)}</Text>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 4 }}>
              <Text>Total after discount:</Text>
              <Text>{formatNumber(totalAfterDiscount)}</Text>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Text>VAT 7%:</Text>
              <Text>{formatNumber(vat)}</Text>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between">
              <Text strong style={{ fontSize: 15 }}>Grand Total:</Text>
              <Text strong style={{ fontSize: 15 }}>
                ฿{formatNumber(total_amount)}
              </Text>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Remark Section */}
      {data.remark && (
        <>
          <Title level={5} style={{ marginBottom: 12, color: '#1890ff', fontWeight: 'bold' }}>
            Remark
          </Title>
          <Card 
            size="small" 
            style={{ 
              marginBottom: 24,
              backgroundColor: '#f0f8ff',
              border: '1px solid #bae7ff',
              borderRadius: 8
            }}
          >
            <div style={{
              fontSize: 13,
              lineHeight: 1.6,
              color: '#0050b3',
              fontStyle: 'italic',
              padding: '8px 0'
            }}>
              <Text style={{ 
                color: '#0050b3',
                fontSize: 13,
                fontWeight: '500'
              }}>
                {data.remark}
              </Text>
            </div>
          </Card>
        </>
      )}

      {/* Payment Information */}
      <Title level={5} style={{ marginBottom: 12 }}>Payment Information</Title>
      <Card size="small" style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          {[1, 2].map(i => {
            const name = data[`bank${i}_name`];
            if (!name) return null;

            return (
              <Col span={12} key={i}>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  <div><Text strong>Bank:</Text> <Text>{data[`bank${i}_name`]}</Text></div>
                  {data[`bank${i}_bank_branch`] && (
                  <div>
                      <Text strong>Branch:</Text> <Text>{data[`bank${i}_bank_branch`]}</Text>
                  </div>
                  )}
                  <div><Text strong>Account Name:</Text> <Text>{data[`bank${i}_account_name`]}</Text></div>
                  <div><Text strong>Account Number:</Text> <Text>{data[`bank${i}_account_number`]}</Text></div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* Terms and Conditions */}
      <Title level={5} style={{ marginBottom: 12 }}>Terms and Conditions</Title>
      <Card 
        size="small" 
        style={{ marginBottom: 24 }}
      >
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          <div style={{ marginBottom: 3 }}>
            1. This quotation is valid for {getExpirationText()}. Please settle the full tuition fee by {calculateExpirationDate()} via bank transfer after receiving your class schedule.
          </div>
          <div style={{ marginBottom: 3 }}>
            2. Please send the transfer slip (or digital slip) together with your nickname, first name, and last name via Line, Facebook, Instagram, or email (info@tfueducation.co.th) as proof of payment.
          </div>
          <div style={{ marginBottom: 3 }}>
            3. The school reserves the right to arrange or modify the class schedule if payment is not made within the specified period.
          </div>
          <div style={{ marginBottom: 3 }}>
            4. Once the registration fee has been paid, it is non-transferable and non-refundable.
          </div>
          <div style={{ marginBottom: 3 }}>
            5. In case of emergency, the school may assign a substitute teacher without prior notice.
          </div>
          <div style={{ marginBottom: 3 }}>
            6. A class reschedule or cancellation request must be made at least 24 hours before the scheduled session.
          </div>
          <div style={{ marginBottom: 3 }}>
            7. Registered lesson hours are valid for 24 months (2 years) and will automatically expire upon the due date.
          </div>
        </div>
      </Card>

      {/* QR Code, Signature, and Print Info */}
      <Row justify="space-between" align="bottom" style={{ marginTop: 32 }}>
        <Col span={6}>
          <div style={{ textAlign: 'center' }}>
            <QRCodeCanvas
              value={qrCodeData}
              size={80}
              level="M"
            />
            <div style={{ fontSize: 10, marginTop: 4, color: '#666' }}>
              QR Code: {qrCodeData}
            </div>
          </div>
        </Col>
        
        <Col span={12}>
          <div style={{ textAlign: 'center', padding: '0 20px' }}>
            <div style={{ 
              fontSize: 12, 
              fontWeight: 'bold', 
              marginBottom: 80,
              color: '#333'
            }}>
              Quoted by
            </div>
            <div style={{ 
              borderTop: '1px solid #333', 
              width: '300px', 
              margin: '0 auto',
              paddingTop: 4,
              fontSize: 14,
              color: '#666'
            }}>
              {created_by_firstname && created_by_lastname && (
                <Col span={24}>
                    <div>
                    <Text>
                        {created_by_firstname} {created_by_lastname}
                    </Text>
                    </div>
                </Col>
              )}
            </div>
          </div>
        </Col>
        
        <Col span={6}>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#666' }}>
            <div style={{ fontWeight: 'bold' }}>Printed On:</div>
            <div>{moment().format('DD MMM YY')}</div>
            <div>{moment().format('HH:mm:ss')}</div>
          </div>
        </Col>
      </Row>
      
      <div style={{ 
        textAlign: 'right', 
        marginBottom: 16,
        '@media print': { display: 'none' }
      }} className="no-print">
        <Button 
          type="primary" 
          icon={<PrinterOutlined />} 
          onClick={handlePrint}
        >
          Print Quotation
        </Button>
      </div>
    </div>
  );
};

export default QuotationPreview;